import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
  toast,
} from "@devhop/ui";
import { EditIcon, EyeIcon, TrashIcon } from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { ContractsListResult } from "@/modules/contracts/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { RowActions } from "@/shared/ui/RowActions";
import { useDeleteContract } from "../api/queries";
import { formatDateLocal } from "@/shared/lib/date-time";

type ContractRow = ContractsListResult["data"][number];

const getStatusBadge = (status: string) => {
  const statusMap: Record<string, { label: string; variant: "default" | "destructive" | "secondary" | "outline" }> = {
    active: { label: "ກຳລັງໃຊ້ງານ", variant: "default" },
    expiring_soon: { label: "ໃກ້ຫມົດອາຍຸ", variant: "secondary" },
    expired: { label: "ຫມົດອາຍຸ", variant: "destructive" },
    renewing: { label: "ກຳລັງຕໍ່ອາຍຸ", variant: "secondary" },
    terminated: { label: "ສິ້ນສຸດ", variant: "outline" },
    draft: { label: "ຮ່າງ", variant: "outline" },
  };
  const statusInfo = statusMap[status] || { label: status, variant: "outline" as const };
  return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
};

type ContractsTableProps = {
  data: ContractRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (contract: ContractRow) => void;
  onView: (contract: ContractRow) => void;
};

export function ContractsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onView,
}: ContractsTableProps) {
  const { has } = usePermissions();
  const deleteContract = useDeleteContract();

  const columns: TanstackReactTable.ColumnDef<ContractRow>[] = [
    createSortableColumn<ContractRow>("title", "ຊື່ສັນຍາ", { size: 200 }),
    createSortableColumn<ContractRow>("typeName", "ປະເພດ", { size: 150 }),
    createSortableColumn<ContractRow>("counterparty", "ຄູ່ສັນຍາ", { size: 150 }),
    createSortableColumn<ContractRow>("startDate", "ວັນທີ່ເລີ່ມ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-sm">{formatDateLocal(row.original.startDate)}</span>
      ),
    }),
    createSortableColumn<ContractRow>("endDate", "ວັນທີ່ຫມົດອາຍຸ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-sm">{formatDateLocal(row.original.endDate)}</span>
      ),
    }),
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => getStatusBadge(row.original.status),
      size: 120,
    },
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: ContractRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          onClick: () => void;
          variant?: "destructive" | "secondary" | "outline";
        }[] = [];

        if (has(Permissions.contracts.read))
          actions.push({
            label: "ເບິ່ງ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original),
          });

        if (has(Permissions.contracts.update))
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });

        if (has(Permissions.contracts.delete))
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const contract = row.original;
              const ok = await confirm({
                title: "ລຶບສັນຍາ",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບສັນຍາ "${contract.title}"?`,
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok)
                toast.promise(deleteContract.run(contract.id), {
                  loading: "ກໍາລັງລຶບສັນຍາ...",
                  success: "ລຶບສັນຍາສໍາເລັດ",
                  error: "ລຶບສັນຍາລົ້ມເຫຼວ",
                });
            },
          });

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <div data-tourid="contracts-table">
      <DataTable<ContractRow, unknown>
        noDataMessage="ບໍ່ພົບສັນຍາ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </div>
  );
}
